import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type ChangeEvent,
  type Dispatch,
  type SetStateAction,
} from "react"

import { logger } from "@/lib/logger"
import type { Persona, PersonaMetadata } from "@/types/api/Persona"
import type { PersonaConfig } from "@/types/PersonaConfig"

import {
  createDefaultPersonaFormData,
  createPersonaFormDataFromNormalizedPersona,
} from "../utils/personaModalUtils"
import type { PersonaFormData } from "../utils/personaValidation"

interface NormalizePersonaModule {
  normalizePersonaForFrontend: (persona: Persona) => Persona
}

const loadNormalizePersonaModule = () =>
  import("@/utils/persona/persona-normalization")

const log = logger("usePersonaFormState")
const NEW_PERSONA_FORM_INITIALIZATION_KEY = "new-persona"

export interface ControlledPersonaFormState {
  data: PersonaFormData
  onChange: Dispatch<SetStateAction<PersonaFormData>>
}

interface UsePersonaFormStateOptions {
  selectedPersona: Persona | null
  isOpen: boolean
  loadNormalizer?: () => Promise<NormalizePersonaModule>
  controlledForm?: ControlledPersonaFormState
}

export const usePersonaFormState = ({
  selectedPersona,
  isOpen,
  loadNormalizer = loadNormalizePersonaModule,
  controlledForm,
}: UsePersonaFormStateOptions) => {
  const formInitializationKey = selectedPersona
    ? `persona:${selectedPersona.id}`
    : NEW_PERSONA_FORM_INITIALIZATION_KEY
  const selectedPersonaRef = useRef(selectedPersona)
  selectedPersonaRef.current = selectedPersona
  const [internalFormData, setInternalFormData] = useState<PersonaFormData>(
    () => createDefaultPersonaFormData(),
  )
  const formData = controlledForm?.data ?? internalFormData
  const setFormData = controlledForm?.onChange ?? setInternalFormData
  const isControlled = controlledForm !== undefined
  const [initializedFormKey, setInitializedFormKey] = useState<string | null>(
    selectedPersona ? null : formInitializationKey,
  )
  const [hasInitializationError, setHasInitializationError] = useState(false)
  const [isPromptExpanded, setIsPromptExpanded] = useState(false)

  useEffect(() => {
    if (!isOpen || isControlled) {
      return
    }

    let isMounted = true

    const initializeFormData = async () => {
      const personaToInitialize = selectedPersonaRef.current
      setInitializedFormKey(personaToInitialize ? null : formInitializationKey)
      setHasInitializationError(false)
      try {
        if (personaToInitialize) {
          const { normalizePersonaForFrontend } = await loadNormalizer()
          const latestPersona = selectedPersonaRef.current

          if (
            !isMounted ||
            !latestPersona ||
            `persona:${latestPersona.id}` !== formInitializationKey
          ) {
            return
          }

          setFormData(
            createPersonaFormDataFromNormalizedPersona(
              normalizePersonaForFrontend(latestPersona),
            ),
          )
          setInitializedFormKey(formInitializationKey)
          return
        }

        setFormData(createDefaultPersonaFormData())
        setInitializedFormKey(formInitializationKey)
      } catch (error: unknown) {
        if (!isMounted) {
          return
        }

        const initializationError =
          error instanceof Error
            ? error
            : new Error("Failed to initialize persona form data")
        log.error("Failed to initialize persona form data", initializationError)
        setHasInitializationError(true)
        setFormData(createDefaultPersonaFormData())
      }
    }

    void initializeFormData()

    return () => {
      isMounted = false
    }
  }, [formInitializationKey, isControlled, isOpen, loadNormalizer, setFormData])

  const handleChange = useCallback(
    (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const { name, value } = event.target

      setFormData((previous) => ({
        ...previous,
        [name]: value,
      }))
    },
    [setFormData],
  )

  const updateMetadata = useCallback(
    (metadata: Partial<PersonaMetadata & PersonaConfig>) => {
      setFormData((previous) => ({
        ...previous,
        metadata: {
          ...previous.metadata,
          ...metadata,
        },
      }))
    },
    [setFormData],
  )

  return {
    formData,
    hasInitializationError,
    isFormInitialized:
      isControlled ||
      (!hasInitializationError && initializedFormKey === formInitializationKey),
    setFormData,
    handleChange,
    updateMetadata,
    isPromptExpanded,
    setIsPromptExpanded,
  }
}
